import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Trash2, Users, Plus } from "lucide-react";
import { statusColor, engagementColor, engagementOf, relationshipOf, relationshipBadge } from "./accountsModel";

const RelBadge = ({ item }) => {
  const rel = relationshipOf(item);
  return <Badge className={`${relationshipBadge(rel)} border text-[10px] uppercase tracking-wide`} data-testid="relationship-badge">{rel}</Badge>;
};

const Th = ({ children }) => <th className="text-left p-4 text-xs text-gray-500 tracking-wider">{children}</th>;

const Head = ({ columns }) => (
  <thead className="border-b border-white/10 sticky top-0 bg-black/60 backdrop-blur-sm">
    <tr>{columns.map((c) => <Th key={c}>{c}</Th>)}</tr>
  </thead>
);

const Row = ({ idx, testId, children }) => (
  <motion.tr
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: Math.min(idx, 20) * 0.03 }}
    className="border-b border-white/5 hover:bg-white/5 transition-colors"
    data-testid={testId}
  >
    {children}
  </motion.tr>
);

const RowActions = ({ onView, onDelete, kind, idx }) => (
  <td className="p-4">
    <div className="flex gap-2">
      <Button size="sm" variant="ghost" onClick={onView} className="text-ism-teal/80 hover:text-ism-teal-light hover:bg-ism-teal/10" data-testid={`view-${kind}-btn-${idx}`}>
        <Eye className="h-4 w-4" />
      </Button>
      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-red-400/70 hover:text-red-400 hover:bg-red-500/10" data-testid={`delete-${kind}-btn-${idx}`}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  </td>
);

const EmptyRows = ({ title, hint, cta, onAdd, testId }) => (
  <div className="text-center py-12" data-testid={testId}>
    <Users className="h-12 w-12 text-gray-600 mx-auto mb-3" />
    <p className="text-gray-400">{title}</p>
    {hint && <p className="text-gray-500 text-sm mt-1">{hint}</p>}
    <Button onClick={onAdd} variant="ghost" className="text-ism-teal mt-4" data-testid={`${testId}-add-btn`}>
      <Plus className="h-4 w-4 mr-2" /> {cta}
    </Button>
  </div>
);

export const AccountsTable = ({ rows, onView, onDelete, onAdd }) => (
  <>
    <table className="w-full">
      <Head columns={["Company", "Relationship", "Industry", "Persona", "Status", "Engagement", "Last Contact", "Actions"]} />
      <tbody>
        {rows.map((account, idx) => (
          <Row key={account.accountId} idx={idx} testId={`account-row-${idx}`}>
            <td className="p-4 cursor-pointer" onClick={() => onView(account)}>
              <p className="text-white hover:text-ism-teal transition-colors">{account.companyName}</p>
              {account.website && <p className="text-[11px] text-gray-500">{account.website}</p>}
            </td>
            <td className="p-4"><RelBadge item={account} /></td>
            <td className="p-4 text-gray-400 text-sm">{account.industry}</td>
            <td className="p-4 text-gray-400 text-sm">{account.persona || account.targetPersona}</td>
            <td className="p-4"><Badge className={`${statusColor(account.status)} border text-xs`}>{account.status}</Badge></td>
            <td className="p-4"><span className={engagementColor(engagementOf(account))}>{engagementOf(account)}</span></td>
            <td className="p-4 text-gray-400 text-sm">{account.lastContact ? new Date(account.lastContact).toLocaleDateString() : "Never"}</td>
            <RowActions kind="account" idx={idx} onView={() => onView(account)} onDelete={() => onDelete(account)} />
          </Row>
        ))}
      </tbody>
    </table>
    {rows.length === 0 && <EmptyRows title="No accounts found" cta="Add your first account" onAdd={onAdd} testId="accounts-empty" />}
  </>
);

export const LeadsTable = ({ rows, onView, onDelete, onAdd }) => (
  <>
    <table className="w-full">
      <Head columns={["Company", "Contact", "Relationship", "Email", "Industry", "Status", "Score", "Actions"]} />
      <tbody>
        {rows.map((lead, idx) => (
          <Row key={lead.leadId} idx={idx} testId={`lead-row-${idx}`}>
            <td className="p-4"><p className="text-white">{lead.companyName}</p></td>
            <td className="p-4 text-gray-300 text-sm">
              {lead.contactName || "-"}
              {lead.contactTitle && <span className="block text-gray-500 text-xs">{lead.contactTitle}</span>}
            </td>
            <td className="p-4"><RelBadge item={lead} /></td>
            <td className="p-4 text-ism-teal/80 text-sm">{lead.contactEmail || "-"}</td>
            <td className="p-4 text-gray-400 text-sm">{lead.industry || "-"}</td>
            <td className="p-4"><Badge className={`${statusColor(lead.status)} border text-xs`}>{lead.status}</Badge></td>
            <td className="p-4"><span className={engagementColor(lead.score || 0)}>{lead.score || 0}</span></td>
            <RowActions kind="lead" idx={idx} onView={() => onView(lead)} onDelete={() => onDelete(lead)} />
          </Row>
        ))}
      </tbody>
    </table>
    {rows.length === 0 && <EmptyRows title="No leads found" hint="Use Hunt mode to generate leads or add manually" cta="Add a lead manually" onAdd={onAdd} testId="leads-empty" />}
  </>
);
